import React, { useState } from "react";
import { ref, update, push, set, increment } from "firebase/database";
import { auth, database } from "../../../firebaseConfig";
import toast from "react-hot-toast";
import { TRANSACTION_DIRECTION, ADVANCE_TRANSACTION_TYPE } from "../../../constants/farmerConstants";

/**
 * Helper function to determine transaction direction based on advance transaction type
 * @param {string} transactionType - "add" or "deduct"
 * @returns {string} - "credit" or "debit"
 */
const getAdvanceTransactionDirection = (transactionType) => {
  return transactionType === ADVANCE_TRANSACTION_TYPE.ADD 
    ? TRANSACTION_DIRECTION.CREDIT  // credit increases advance (liability)
    : TRANSACTION_DIRECTION.DEBIT;  // debit decreases advance
};

const AdvancePaymentModal = ({ farmer, companyName, onClose }) => {
  const [amount, setAmount] = useState("");
  const [transactionType, setTransactionType] = useState("add"); // 'add' or 'deduct'
  const [details, setDetails] = useState("");
  const [loading, setLoading] = useState(false);

  const handleTransaction = async () => {
    if (!amount || isNaN(amount) || parseFloat(amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    const transactionAmount = parseFloat(amount);
    const currentBalance = parseFloat(farmer.advanceBalance || 0);
    
    // Validate deduction amount
    if (transactionType === "deduct") {
      if (transactionAmount > currentBalance) {
        toast.error("Deduction amount exceeds current advance balance");
        return;
      }
    }

    setLoading(true);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("No authenticated user found");

      const now = new Date();
      const year = now.getFullYear();
      const month = now.toLocaleString("en-US", { month: "long" });
      const day = now.getDate();
      const dateStr = now.toISOString();

      // Prepare write operations
      const writePromises = [];

      // Calculate the increment value (positive for add, negative for deduct)
      const advanceIncrement = transactionType === ADVANCE_TRANSACTION_TYPE.ADD 
        ? transactionAmount 
        : -transactionAmount;

      // Calculate expected new balance for transaction record
      const newBalance = currentBalance + advanceIncrement;

      // 1️⃣ Update farmer's advance balance using increment for concurrent safety
      const farmerPath = `Companies/${companyName}/ValidatedFarmers/${farmer.otherName}`;
      writePromises.push(update(ref(database, farmerPath), {
        AdvanceBalance: increment(advanceIncrement),
      }));

      // Record the transaction
      const transactionId = push(ref(database, `Companies/${companyName}/AdvanceTransactions`)).key;
      const transactionData = {
        Id: transactionId,
        OtherName: farmer.otherName || "",
        CloudName: farmer.name || "",
        TransactionType: transactionType,
        Amount: amount.toString(),
        PreviousBalance: currentBalance.toString(),
        NewBalance: newBalance.toString(),
        Details: details || "N/A",
        CompanyName: companyName,
        ProcessedBy: user.email,
        TransactionDate: dateStr,
      };

      // 2️⃣ Save transaction history to farmer records
      const transactionPath = `Companies/${companyName}/FarmerRecords/${farmer.otherName}/AdvanceTransactions/${year}/${month}/${transactionId}`;
      writePromises.push(set(ref(database, transactionPath), transactionData));

      // 3️⃣ Also save to company-level advance transactions
      const companyTransactionPath = `Companies/${companyName}/AdvanceTransactions/${year}/${month}/${day}/${transactionId}`;
      writePromises.push(set(ref(database, companyTransactionPath), transactionData));

      // 4️⃣ Record transaction in company FarmerAdvance account
      const advanceTxPath = `Companies/${companyName}/Accounts/farmer_advance/${year}/${month}/${transactionId}`;
      const advanceTxData = {
        id: transactionId,
        amount: transactionAmount,
        direction: getAdvanceTransactionDirection(transactionType),
        farmerName: farmer.name,
        farmerOtherName: farmer.otherName,
        description: `${transactionType === ADVANCE_TRANSACTION_TYPE.ADD ? "Advance payment to" : "Advance deduction from"} ${farmer.name}`,
        details: details || "N/A",
        date: dateStr,
        processedBy: user.email,
      };
      writePromises.push(set(ref(database, advanceTxPath), advanceTxData));

      // Execute all writes in parallel
      await Promise.all(writePromises);

      toast.success(
        `Advance ${transactionType === "add" ? "payment" : "deduction"} recorded successfully!`
      );
      onClose();
    } catch (err) {
      console.error("Advance transaction error:", err);
      toast.error("Failed to record advance transaction");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 z-50">
      <div className="bg-white rounded-lg shadow-lg w-96 p-6">
        <h2 className="text-lg font-semibold mb-4 text-purple-700">
          Manage Advance Payment - {farmer.name}
        </h2>

        <div className="mb-4 p-3 bg-purple-50 rounded-lg">
          <p className="text-sm text-gray-600">Current Advance Balance:</p>
          <p className="text-2xl font-bold text-purple-700">
            KES {Number(farmer.advanceBalance || 0).toLocaleString()}
          </p>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">Transaction Type</label>
            <select
              className="border rounded-md px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-purple-500"
              value={transactionType}
              onChange={(e) => setTransactionType(e.target.value)}
            >
              <option value="add">Add New Advance Payment</option>
              <option value="deduct">Deduct from Advance Balance</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Amount (KES)</label>
            <input
              type="number"
              className="border rounded-md px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-purple-500"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter amount"
              step="0.01"
              min="0"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Details/Notes</label>
            <textarea
              className="border rounded-md px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-purple-500"
              rows="3"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Add any notes about this transaction..."
            />
          </div>

          {amount && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">New Balance Will Be:</p>
              <p className="text-xl font-bold text-purple-700">
                KES{" "}
                {Number(
                  transactionType === "add"
                    ? (parseFloat(farmer.advanceBalance || 0) + parseFloat(amount || 0))
                    : (parseFloat(farmer.advanceBalance || 0) - parseFloat(amount || 0))
                ).toLocaleString()}
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end mt-5 space-x-2">
          <button
            className="px-4 py-2 rounded-md bg-gray-300 hover:bg-gray-400"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 rounded-md bg-purple-600 text-white hover:bg-purple-700"
            onClick={handleTransaction}
            disabled={loading}
          >
            {loading ? "Processing..." : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdvancePaymentModal;
